/**
 * Single source of truth for rail display labels. Rail names are proper nouns (brand/product names) and are
 * deliberately NOT translated — the same label renders identically across es/en/pt. The only translatable
 * piece, the generic "transfer" fallback, is passed in by the caller (a pure util can't reach
 * `useTranslations`), so i18n is honored where it actually applies.
 *
 * Two entry points share this table so the wizard and the runtime surfaces can never drift:
 *
 * - `formatRail(rail)` maps a runtime rail enum (`CBU_CVU`, `BREB`, …) → label. Unknown/new rails pass through
 *   raw — the same fallback pattern as the runtime status labels. Used by payments-activity, payout-rescue,
 *   wallet methods. CSV exports intentionally keep the raw enum so files re-import losslessly.
 * - `railLabelForCountry(iso3, fallback)` maps a payout destination country (ISO3) → its rail label, for the
 *   dispersions wizard header/review.
 */
const RAIL_LABEL = {
  PIX: "PIX",
  SPEI: "SPEI",
  CBU_CVU: "CBU/CVU",
  BREB: "Bre-B",
  TRANSFIYA: "Transfiya",
  NEQUI: "Nequi",
  BANCOLOMBIA_BUTTON: "Botón Bancolombia",
} as const;

/**
 * Payout destination country (ISO3) → its DEFAULT runtime rail. Colombia now has two payout rails (BREB +
 * TRANSFIYA); this map keeps the country-level default for headers, while rail-aware views format the actual
 * rail via formatRail.
 */
const COUNTRY_RAIL: Record<string, keyof typeof RAIL_LABEL> = {
  BRA: "PIX",
  MEX: "SPEI",
  ARG: "CBU_CVU",
  COL: "BREB",
};

export function formatRail(rail: string): string {
  return (RAIL_LABEL as Record<string, string>)[rail] ?? rail;
}

export function railLabelForCountry(iso3: string | undefined, fallback: string): string {
  const rail = iso3 ? COUNTRY_RAIL[iso3] : undefined;
  return rail ? RAIL_LABEL[rail] : fallback;
}
